import React from 'react'
import { formatIsoDate, type ISODate } from '../lib/format'
import { dateAt, type DateRange } from '../lib/geometry'
import type { PrintLink, PrintRow } from './print-data'
import type { PrintLabels } from './print-labels'
import { FOOTER_BAND_H, type PrintLayout, type PrintPageRef } from './print-layout'
import type { PrintOptions } from './print-options'
import PrintFooter from './PrintFooter'
import PrintGrid from './PrintGrid'
import PrintHeaderBand from './PrintHeaderBand'
import PrintTimeline from './PrintTimeline'

export interface PrintPageProps {
  layout: PrintLayout
  page: PrintPageRef
  options: PrintOptions
  /** All visible rows; this page slices out its own chunk. */
  rows: PrintRow[]
  links: PrintLink[]
  range: DateRange
  today: ISODate
  statusDate: ISODate | null
  header: { title: string; status: string; meta: string }
  labels: PrintLabels
  locale: string
  t: (key: string, opts?: any) => string
}

/**
 * One paper page (spec §4). Pure: props in, one `<svg>` out — no hooks, no store, no
 * `useTranslation`, no DOM access, so it renders identically in the browser and under
 * `renderToStaticMarkup`. `className="print-page schedule-print"` is what the print CSS and the
 * Roboto face key on.
 */
const PrintPage: React.FC<PrintPageProps> = ({ layout, page, options, rows, links, range, today, statusDate, header, labels, locale, t }) => {
  const clipId = `print-body-${page.index}`
  const pageRows = rows.slice(page.rowFrom, page.rowTo)
  const strip = layout.strips[page.strip]
  // On a paginated timeline every page names its strip's date span, so a sheet can be placed
  // without the table — the only row identification a continuation strip has is the bar labels.
  const meta = layout.strips.length > 1
    ? `${header.meta} · ${formatIsoDate(t, dateAt(strip.fromDay, range.start))} – ${formatIsoDate(t, dateAt(strip.toDay - 1, range.start))}`
    : header.meta
  return (
    <svg
      className="print-page schedule-print bg-background"
      data-print-page={page.index}
      xmlns="http://www.w3.org/2000/svg"
      width={layout.pageW}
      height={layout.pageH}
      viewBox={`0 0 ${layout.pageW} ${layout.pageH}`}
    >
      <rect x={0} y={0} width={layout.pageW} height={layout.pageH} className="fill-background" />
      <PrintHeaderBand x={layout.bodyX} y={layout.margin} width={layout.bodyW} title={header.title} status={header.status} meta={meta} />
      <clipPath id={clipId}>
        <rect x={layout.bodyX} y={layout.bodyY} width={layout.bodyW} height={layout.bodyH} />
      </clipPath>
      <g clipPath={`url(#${clipId})`}>
        <g transform={`translate(${layout.bodyX},${layout.bodyY}) scale(${layout.scale})`}>
          {strip.gridWidth > 0 && (
            <PrintGrid layout={layout} page={page} rows={pageRows} labels={labels} includeCritical={options.includeCritical} t={t} />
          )}
          <PrintTimeline
            layout={layout} page={page} options={options} rows={pageRows} links={links}
            range={range} today={today} statusDate={statusDate} labels={labels} locale={locale}
          />
        </g>
      </g>
      <PrintFooter
        x={layout.bodyX}
        y={layout.pageH - layout.margin - FOOTER_BAND_H}
        width={layout.bodyW}
        showLegend={options.includeLegend}
        labels={labels}
        pageText={labels.page(page.index + 1, layout.pages.length)}
      />
    </svg>
  )
}

export default PrintPage
